import { API_BASE_URL, authFetch, authHeaders, clearToken } from './auth';
import {
  CommissionStatus,
  FollowUpFilter,
  PartnerCategory,
  PartnerStatus,
  PartnerTier,
  ProspectSource,
  ProspectStatus,
  ReferralStatus,
} from '../constants/partnerNetwork';
import { timezoneOffset } from '../utils/partnerFormat';

/**
 * Admin-console client for the partner network. Rides on the existing
 * authFetch, so it inherits the bearer token and the 401 handling.
 */

/* ------------------------------------------------------------------ */
/* Shared shapes                                                       */
/* ------------------------------------------------------------------ */

export interface Pagination {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface ActivityEntry {
  id: string;
  type: string;
  description: string;
  actorName: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
}

const query = (params: object) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, String(value));
    }
  });
  const serialised = search.toString();
  return serialised ? `?${serialised}` : '';
};

/* ------------------------------------------------------------------ */
/* Prospects                                                           */
/* ------------------------------------------------------------------ */

export interface ProspectNote {
  id: string;
  body: string;
  authorName: string;
  createdAt: string;
}

export interface Prospect {
  id: string;
  prospectId: string;
  fullName: string;
  companyName: string;
  jobTitle: string;
  email: string;
  phone: string;
  country: string;
  city: string;
  website: string;
  linkedinUrl: string;
  category: PartnerCategory | null;
  tier: PartnerTier | null;
  industry: string;
  source: ProspectSource | null;
  assignedMarketerId: string | null;
  assignedMarketerName?: string;
  status: ProspectStatus;
  lastContactedAt: string | null;
  nextFollowUpAt: string | null;
  notes: ProspectNote[];
  convertedPartnerId: string | null;
  convertedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProspectFilters {
  search?: string;
  status?: string;
  category?: string;
  tier?: string;
  source?: string;
  country?: string;
  assignedMarketerId?: string;
  followUp?: FollowUpFilter | '';
  from?: string;
  to?: string;
  sort?: string;
  direction?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export const listProspects = (
  filters: ProspectFilters = {}
): Promise<{ data: Prospect[]; pagination: Pagination }> =>
  authFetch(
    `/api/partner-prospects${query({ ...filters, tzOffset: timezoneOffset() })}`
  );

export interface ProspectStats {
  followUps: {
    today: number;
    tomorrow: number;
    next3: number;
    overdue: number;
    unscheduled: number;
  };
  total: number;
  byStatus: Record<ProspectStatus, number>;
  referenceDate: string;
}

export const getProspectStats = (mine = false): Promise<ProspectStats> =>
  authFetch(
    `/api/partner-prospects/stats${query({
      tzOffset: timezoneOffset(),
      mine: mine ? 'true' : undefined,
    })}`
  );

export const getProspect = (
  id: string
): Promise<{ prospect: Prospect; activities: ActivityEntry[]; canEdit: boolean }> =>
  authFetch(`/api/partner-prospects/${id}`);

export interface DuplicateMatch {
  field: 'email' | 'phone' | 'companyName' | 'linkedinUrl';
  prospect: Prospect;
}

/**
 * Thrown when the server refuses a create because a similar prospect exists.
 * Carries the matches so the form can show them and offer to add anyway.
 */
export class DuplicateProspectError extends Error {
  duplicates: DuplicateMatch[];

  constructor(message: string, duplicates: DuplicateMatch[]) {
    super(message);
    this.name = 'DuplicateProspectError';
    this.duplicates = duplicates;
  }
}

export type ProspectInput = Partial<
  Omit<Prospect, 'id' | 'prospectId' | 'notes' | 'createdAt' | 'updatedAt'>
> & { force?: boolean };

/**
 * Creates a prospect.
 *
 * Uses fetch directly rather than authFetch because a 409 carries the matching
 * records in its body, and authFetch collapses every failure to a bare message.
 * The form needs those matches to show "a similar prospect already exists" with
 * the record beside it.
 */
export const createProspect = async (
  input: ProspectInput
): Promise<Prospect> => {
  const response = await fetch(`${API_BASE_URL}/api/partner-prospects`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(input),
  });

  const data = await response.json().catch(() => ({}));

  if (response.status === 401) {
    clearToken();
    throw new Error(data.error || 'Your session has expired. Sign in again.');
  }

  if (response.status === 409 && Array.isArray(data.duplicates)) {
    throw new DuplicateProspectError(
      data.error || 'A similar prospect already exists.',
      data.duplicates
    );
  }

  if (!response.ok) {
    throw new Error(data.error || 'Could not create the prospect');
  }

  return data.prospect;
};

export const checkDuplicates = (input: {
  email?: string;
  phone?: string;
  companyName?: string;
  linkedinUrl?: string;
  excludeId?: string;
}): Promise<{ duplicates: DuplicateMatch[] }> =>
  authFetch('/api/partner-prospects/check-duplicate', {
    method: 'POST',
    body: JSON.stringify(input),
  });

export const updateProspect = async (
  id: string,
  input: ProspectInput
): Promise<Prospect> => {
  const data = await authFetch(`/api/partner-prospects/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
  return data.prospect;
};

export const updateProspectStatus = async (
  id: string,
  status: ProspectStatus,
  extras: { note?: string; nextFollowUpAt?: string | null } = {}
): Promise<Prospect> => {
  const data = await authFetch(`/api/partner-prospects/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status, ...extras }),
  });
  return data.prospect;
};

export const updateFollowUp = async (
  id: string,
  input: { nextFollowUpAt?: string | null; lastContactedAt?: string | null }
): Promise<Prospect> => {
  const data = await authFetch(`/api/partner-prospects/${id}/follow-up`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
  return data.prospect;
};

export const addProspectNote = async (
  id: string,
  body: string
): Promise<Prospect> => {
  const data = await authFetch(`/api/partner-prospects/${id}/notes`, {
    method: 'POST',
    body: JSON.stringify({ body }),
  });
  return data.prospect;
};

/**
 * Records something the browser did on its own — opening WhatsApp leaves the
 * app entirely, so nothing else would ever reach the timeline.
 */
export const logProspectActivity = (
  id: string,
  input: {
    type: string;
    description: string;
    metadata?: Record<string, unknown>;
    markContacted?: boolean;
  }
): Promise<{ prospect: Prospect }> =>
  authFetch(`/api/partner-prospects/${id}/activity`, {
    method: 'POST',
    body: JSON.stringify(input),
  });

export interface BulkEmailResult {
  email: string;
  name?: string;
  success: boolean;
  error?: string;
}

export interface BulkEmailResponse {
  success: boolean;
  sent: number;
  failed: number;
  skipped: number;
  message: string;
  results: BulkEmailResult[];
}

/**
 * Server caps a single request at 25 recipients. The compose modal batches to
 * this size so one slow batch cannot run past the serverless timeout.
 */
export const PROSPECT_EMAIL_BATCH_SIZE = 5;

export const bulkEmailProspects = (payload: {
  prospectIds: string[];
  subject: string;
  message: string;
  greeting?: string;
  signOff?: string;
  bcc?: string;
  delayMs?: number;
  markContacted?: boolean;
  nextFollowUpAt?: string | null;
}): Promise<BulkEmailResponse> =>
  authFetch('/api/partner-prospects/bulk-email', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export interface ConvertResult {
  partner: {
    id: string;
    partnerId: string;
    email: string;
    commissionRate?: number;
  };
  /** Only returned when the welcome email failed, so it can be passed on. */
  temporaryPassword: string | null;
  welcomeSent?: boolean;
  adoptedExistingAccount?: boolean;
  prospect: Prospect;
}

export const convertProspect = (
  id: string,
  input: { email?: string; commissionRate?: number } = {}
): Promise<ConvertResult> =>
  authFetch(`/api/partner-prospects/${id}/convert`, {
    method: 'POST',
    body: JSON.stringify(input),
  });

export const deleteProspect = (id: string): Promise<void> =>
  authFetch(`/api/partner-prospects/${id}`, { method: 'DELETE' });

/* ------------------------------------------------------------------ */
/* Partners                                                            */
/* ------------------------------------------------------------------ */

export interface AdminPartner {
  id: string;
  partnerId: string;
  fullName: string;
  companyName: string;
  email: string;
  phone: string;
  country: string;
  city: string;
  website: string;
  linkedinUrl: string;
  category: PartnerCategory | null;
  tier: PartnerTier | null;
  status: PartnerStatus;
  commissionRate: number;
  sourceProspectId: string | null;
  approvedAt: string | null;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
  referralCount?: number;
  activeReferrals?: number;
  wonReferrals?: number;
  totalEarned?: number;
  totalPaid?: number;
}

export const listPartners = (
  filters: {
    search?: string;
    status?: string;
    category?: string;
    tier?: string;
    country?: string;
    page?: number;
    limit?: number;
  } = {}
): Promise<{ data: AdminPartner[]; pagination: Pagination }> =>
  authFetch(`/api/partners${query(filters)}`);

export const getPartner = (
  id: string
): Promise<{
  partner: AdminPartner;
  referrals: Array<{
    id: string;
    referralId: string;
    businessName: string;
    status: ReferralStatus;
    projectValue: number | null;
    submittedAt: string;
  }>;
  commissions: Array<{
    id: string;
    referralId: string;
    commissionAmount: number;
    status: CommissionStatus;
    paidDate: string | null;
  }>;
  activities: ActivityEntry[];
  sourceProspect: { id: string; prospectId: string; fullName: string } | null;
}> => authFetch(`/api/partners/${id}`);

export const updatePartner = async (
  id: string,
  input: Partial<AdminPartner>
): Promise<AdminPartner> => {
  const data = await authFetch(`/api/partners/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
  return data.partner;
};

export const updatePartnerStatus = async (
  id: string,
  status: PartnerStatus,
  reason?: string
): Promise<AdminPartner> => {
  const data = await authFetch(`/api/partners/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status, reason }),
  });
  return data.partner;
};

/* ------------------------------------------------------------------ */
/* Referrals                                                           */
/* ------------------------------------------------------------------ */

export interface AdminReferral {
  id: string;
  referralId: string;
  partnerId: string | null;
  partner: {
    id: string;
    partnerId: string;
    fullName: string;
    companyName: string;
    email?: string;
    phone?: string;
    commissionRate?: number;
  } | null;
  businessName: string;
  businessType: string;
  country: string;
  city: string;
  website: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  problem: string;
  additionalInformation: string;
  identifiedHow: string;
  status: ReferralStatus;
  statusLabel: string;
  statusHistory: Array<{
    status: ReferralStatus;
    label: string;
    changedByName: string;
    note: string;
    createdAt: string;
  }>;
  estimatedValue: number | null;
  projectValue: number | null;
  commissionRate: number | null;
  effectiveRate?: number;
  internalNotes: Array<{
    id: string;
    body: string;
    authorName: string;
    createdAt: string;
  }>;
  commission?: {
    id: string;
    status: CommissionStatus;
    commissionAmount: number;
    paidDate: string | null;
  } | null;
  submittedAt: string;
  updatedAt: string;
}

export interface ReferralFilters {
  search?: string;
  status?: string;
  partnerId?: string;
  country?: string;
  businessType?: string;
  commissionStatus?: string;
  minValue?: number;
  maxValue?: number;
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
}

export const listReferrals = (
  filters: ReferralFilters = {}
): Promise<{ data: AdminReferral[]; pagination: Pagination }> =>
  authFetch(`/api/referrals${query(filters)}`);

export interface ReferralCommission {
  id: string;
  commissionRate: number;
  eligibleAmount: number;
  commissionAmount: number;
  currency: string;
  status: CommissionStatus;
  dueDate: string | null;
  paidDate: string | null;
  paymentReference: string;
  notes: string;
  paidByName: string;
}

export const getReferral = (
  id: string
): Promise<{
  referral: AdminReferral;
  commission: ReferralCommission | null;
  activities: ActivityEntry[];
}> => authFetch(`/api/referrals/${id}`);

export const updateReferral = (
  id: string,
  input: Partial<AdminReferral>
): Promise<{ referral: AdminReferral }> =>
  authFetch(`/api/referrals/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });

export const updateReferralStatus = (
  id: string,
  status: ReferralStatus,
  note?: string
): Promise<{ referral: AdminReferral }> =>
  authFetch(`/api/referrals/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status, note }),
  });

export const setReferralCommission = (
  id: string,
  input: { projectValue?: number; commissionRate?: number; markDue?: boolean }
): Promise<{ referral: AdminReferral; commission: ReferralCommission | null }> =>
  authFetch(`/api/referrals/${id}/commission`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });

export const addReferralNote = (
  id: string,
  body: string
): Promise<{ referral: AdminReferral }> =>
  authFetch(`/api/referrals/${id}/notes`, {
    method: 'POST',
    body: JSON.stringify({ body }),
  });

/* ------------------------------------------------------------------ */
/* Commissions                                                         */
/* ------------------------------------------------------------------ */

export interface AdminCommission {
  id: string;
  partner: {
    id: string;
    partnerId: string;
    fullName: string;
    companyName: string;
    email: string;
  } | null;
  referral: {
    id: string;
    referralId: string;
    businessName: string;
    status: ReferralStatus;
  } | null;
  commissionRate: number;
  eligibleAmount: number;
  commissionAmount: number;
  currency: string;
  status: CommissionStatus;
  dueDate: string | null;
  paidDate: string | null;
  paymentReference: string;
  notes: string;
  paidByName: string;
  createdAt: string;
}

export const listCommissions = (
  filters: {
    status?: string;
    partnerId?: string;
    from?: string;
    to?: string;
    page?: number;
    limit?: number;
  } = {}
): Promise<{
  data: AdminCommission[];
  totals: Record<CommissionStatus, { amount: number; count: number }>;
  pagination: Pagination;
}> => authFetch(`/api/commissions${query(filters)}`);

export const markCommissionPaid = (
  id: string,
  input: { paymentReference: string; paidDate?: string; notes?: string }
): Promise<{ commission: { id: string; status: CommissionStatus } }> =>
  authFetch(`/api/commissions/${id}/paid`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });

export const updateCommission = (
  id: string,
  input: { status?: CommissionStatus; dueDate?: string | null; notes?: string }
): Promise<{ commission: { id: string; status: CommissionStatus } }> =>
  authFetch(`/api/commissions/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });

export const cancelCommission = (
  id: string,
  reason?: string
): Promise<{ commission: { id: string; status: CommissionStatus } }> =>
  authFetch(`/api/commissions/${id}/cancel`, {
    method: 'PATCH',
    body: JSON.stringify({ reason }),
  });

/* ------------------------------------------------------------------ */
/* Notifications                                                       */
/* ------------------------------------------------------------------ */

export interface PartnerNetworkNotification {
  id: string;
  type: string;
  title: string;
  body: string;
  link: string;
  readAt: string | null;
  createdAt: string;
}

export const listNotifications = (): Promise<{
  data: PartnerNetworkNotification[];
  unread: number;
}> => authFetch('/api/partner-network/notifications');

export const markNotificationsRead = (ids?: string[]): Promise<void> =>
  authFetch('/api/partner-network/notifications/read', {
    method: 'PATCH',
    body: JSON.stringify({ ids }),
  });
